import { combineReducers, configureStore } from '@reduxjs/toolkit'
import type { Store } from 'redux'
import rootReducer from './rootReducer'
import { createMessageMiddleware } from './middleware/messageMiddleware'
import { createChatsMiddleware } from './middleware/chatsMiddleware'
import { createListenerMiddlewareInstance } from './middleware/listenerMiddleware'
import { createCacheMiddlewareInstance } from './middleware/cacheMiddleware'
import { createTabsSyncMiddleware } from './middleware/tabsSyncMiddleware'
import { sessionRequested } from './user/user'
import { MessageEngine } from '@/engine/MessageEngine/MessageEngine'
import { ChatEngine } from '@/engine/ChatEngine/ChatEngine'
import ChatsCache from '@/services/ChatsCache/ChatsCache'
import LastMessagesCache from '@/services/LastMessagesCache/LastMessagesCache'
import SpeechToTextService from '@/services/SpeechToTextService/SpeechToTextService'
import type { RootState } from './types'
import type { Engines } from '@/engine/EnginesContext'

export const createAppStore = (initialState?: Partial<RootState>) => {
  const storeRef: { current: Store<RootState> | null } = { current: null }
  const getState = () => {
    if (!storeRef.current) {
      throw new Error('store accessed before initialization')
    }
    return storeRef.current.getState()
  }

  const chatsCache = new ChatsCache()
  const lastMessagesCache = new LastMessagesCache()
  const messageEngine = new MessageEngine({ getState, lastMessagesCache })
  const chatEngine = new ChatEngine({ getState, messageEngine, chatsCache, lastMessagesCache })
  const speechToTextService = new SpeechToTextService()
  const engines: Engines = { messageEngine, chatEngine, chatsCache, lastMessagesCache, speechToTextService }

  const listenerMiddleware = createListenerMiddlewareInstance()
  const cacheMiddleware = createCacheMiddlewareInstance(engines)

  const reducer = combineReducers(rootReducer)
  const store = configureStore({
    reducer,
    preloadedState: initialState,
    middleware: getDefault => getDefault({ serializableCheck: false })
      .prepend(listenerMiddleware.middleware)
      .prepend(cacheMiddleware.middleware)
      .concat(createChatsMiddleware(engines))
      .concat(createMessageMiddleware(engines))
      .concat(createTabsSyncMiddleware())
  })
  storeRef.current = store

  // A user restored from localStorage has an `accessToken` but no session
  // `token` yet — kick the auth listener once to establish the session.
  if (store.getState().user.user) {
    store.dispatch(sessionRequested())
  }

  return { store, engines }
}

export type AppStore = ReturnType<typeof createAppStore>
